import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { EmailService, EmailRequestData } from '@/services/email/emailService';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Hook for managing email service operations
 */
export const useEmailService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  // Mutation for sending interview request emails
  const sendEmailMutation = useMutation({
    mutationFn: (data: EmailRequestData) => EmailService.sendInterviewRequestEmail(data),
    onSuccess: (response) => {
      toast.success(t('email.sendSuccess'));
      console.log('Email sent:', response);
    },
    onError: (error: any) => {
      toast.error(`${t('email.sendError')}: ${error.message}`);
      console.error('Email sending failed:', error);
    }
  });

  // Query for fetching email logs
  const {
    data: emailLogs,
    isLoading: isLoadingLogs,
    refetch: refetchLogs
  } = useQuery({
    queryKey: ['email-logs'],
    queryFn: () => EmailService.getAllEmailLogs(),
    staleTime: 30000, // 30 seconds
  });

  /**
   * Send an interview request email
   */
  const sendInterviewRequestEmail = async (data: EmailRequestData) => {
    return sendEmailMutation.mutateAsync(data);
  };

  /**
   * Get email logs for a specific interview request
   */
  const getEmailLogsForRequest = async (requestId: string) => {
    setIsLoading(true);
    try {
      const logs = await EmailService.getEmailLogs(requestId);
      return logs;
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error(t('errors.loadingError'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Email sending
    sendInterviewRequestEmail,
    isSendingEmail: sendEmailMutation.isPending,
    
    // Email logs
    emailLogs,
    isLoadingLogs,
    refetchLogs,
    getEmailLogsForRequest,
    
    // General loading state
    isLoading
  };
};